/**
 * Custom Stripe Checkout Page
 * Matches ParkProUI design system
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, Lock, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import toast from '../../../utils/toast';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ plan, billingCycle, currentSubscription, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Submit form to validate
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Add payment method to customer
      const addMethodResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/add-payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ paymentMethodId: paymentMethod.id }),
      });

      if (!addMethodResponse.ok) {
        const errorData = await addMethodResponse.json();
        throw new Error(errorData.message || 'Failed to save payment method');
      }

      // Create or update subscription
      const subscribeResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId: plan.plan_id,
          billingCycle,
        }),
      });

      if (!subscribeResponse.ok) {
        const errorData = await subscribeResponse.json();
        throw new Error(errorData.message || 'Failed to create subscription');
      }

      const data = await subscribeResponse.json();
      toast.success(currentSubscription 
        ? `Plan changed to ${plan.display_name} successfully!`
        : `Subscription to ${plan.display_name} activated successfully!`
      );

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 bg-white rounded-lg border border-gray-300">
        <PaymentElement 
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
              radios: true,
              spacedAccordionItems: true
            },
            fields: {
              billingDetails: {
                address: 'auto',
              }
            },
            wallets: {
              applePay: 'auto',
              googlePay: 'auto',
            },
          }}
        />
      </div>

      {error && (
        <div className="flex items-start p-4 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5 mr-3 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-red-900">Payment Error</h4>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-6 py-3 text-gray-700 bg-white rounded-lg border border-gray-300 font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              {currentSubscription ? 'Confirm Change' : 'Activate Subscription'}
            </>
          )}
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Lock className="w-3 h-3" />
        <span>Secured by Stripe • All transactions are encrypted</span>
      </div>
    </form>
  );
};

const SubscriptionCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { plan, billingCycle, currentSubscription } = location.state || {};

  const [setupIntent, setSetupIntent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!plan) {
      toast.error('No plan selected');
      navigate('/agent/settings/subscription');
      return;
    }

    // Create setup intent for payment method collection (authenticated)
    fetch(`${import.meta.env.VITE_API_URL}/api/stripe/create-setup-intent-authenticated`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        metadata: {
          source: 'subscription_checkout',
          plan_id: plan.plan_id,
        }
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setSetupIntent(data.clientSecret);
        } else {
          throw new Error(data.message || 'Failed to initialize payment');
        }
      })
      .catch(err => {
        toast.error(err.message);
        navigate('/agent/settings/subscription');
      })
      .finally(() => setLoading(false));
  }, [plan, navigate, user]);

  const handleSuccess = (data) => {
    navigate('/agent/settings', { 
      state: { subscriptionUpdated: true } 
    });
  };

  const handleCancel = () => {
    navigate('/agent/settings/subscription');
  };

  if (loading || !setupIntent) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader className="w-6 h-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="overflow-auto flex-1 w-full min-h-0 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back */}
        <button
          onClick={handleCancel}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to plans
        </button>

        {/* Header */}
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl mb-1">
            Complete Your Subscription
          </h1>
          <p className="text-sm text-gray-500">
            Add your payment method to activate {plan?.display_name}
          </p>
        </div>

        {/* Summary Card */}
        <div className="mb-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Plan</span>
              <span className="font-semibold text-gray-900">{plan.display_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Billing Cycle</span>
              <span className="font-semibold text-gray-900 capitalize">{billingCycle}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="text-gray-900 font-semibold">Total</span>
              <span className="text-xl font-bold text-gray-900">
                ${billingCycle === 'yearly' 
                  ? Math.round(plan.price_yearly / 12) 
                  : plan.price_monthly}/month
              </span>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupIntent,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <CheckoutForm
              plan={plan}
              billingCycle={billingCycle}
              currentSubscription={currentSubscription}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
